import { useContext } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AuthContext } from 'app/shared/context/Auth';
import { StripeVirtualWallet } from 'app/fan/utils/checkout';
import { isUpcomingEvent } from 'app/fan/utils/eventHelper';
import { Event } from 'app//shared/typings';

interface Params {
  visaPartnershipCheckoutLogic: any;
  stripeVirtualWallet: any;
  event: any;
  isExpressCheckout: any;
}

export interface ShowVirtualPayment {
  showRegularApplePay?: boolean;
  showExpressApplePay: boolean;
  showRegularGooglePay?: boolean;
  showExpressGooglePay: boolean;
}

function canMakeVirtualWalletPayment(
  stripeVirtualWallet: StripeVirtualWallet | null
) {
  const paymentRequest = stripeVirtualWallet?.paymentRequest;

  if (!paymentRequest) {
    return {
      applePayEligible: false,
      googlePayEligible: false,
    };
  }

  const canMakeVirtualPaymentResult =
    stripeVirtualWallet?.canMakeVirtualPaymentResult;

  const applePayEligible =
    !!canMakeVirtualPaymentResult && canMakeVirtualPaymentResult.applePay;
  const googlePayEligible =
    !!canMakeVirtualPaymentResult && canMakeVirtualPaymentResult.googlePay;

  return {
    applePayEligible,
    googlePayEligible,
  };
}

// Returns whether the event is eligible for Third Party payment
function eventIsEligibleForVirtualCheckout(
  event: Event | null,
  searchParams: URLSearchParams
) {
  const isApplyFlow = event?.attendeeFlow === 'apply';
  const isCompetition = event?.competition && event?.competition.id;
  const forceStartedEventState = searchParams.get('sofarStarted') == 'true';
  const eventIsUpcoming =
    !forceStartedEventState && event && isUpcomingEvent(event);
  const eventIsValidForVirtualCheckout =
    event &&
    !event.onPresale &&
    !isApplyFlow &&
    !isCompetition &&
    eventIsUpcoming;

  return !!eventIsValidForVirtualCheckout;
}

// Returns whether Google Pay and Apple Pay will be shown
export const useShowVirtualWalletPayment = ({
  visaPartnershipCheckoutLogic,
  stripeVirtualWallet,
  event,
  isExpressCheckout,
}: Params): ShowVirtualPayment => {
  const { loading: sessionLoading } = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  const eventIsEligible = eventIsEligibleForVirtualCheckout(
    event,
    searchParams
  );
  const { applePayEligible, googlePayEligible } =
    canMakeVirtualWalletPayment(stripeVirtualWallet);
  const userIsVisaPresaleOrFreeTicketEligible =
    visaPartnershipCheckoutLogic.canRedeemVisaFreeTicket ||
    visaPartnershipCheckoutLogic.visaPreSaleEligible;

  if (
    !eventIsEligible ||
    userIsVisaPresaleOrFreeTicketEligible ||
    sessionLoading
  ) {
    return {
      showRegularApplePay: false,
      showExpressApplePay: false,
      showRegularGooglePay: false,
      showExpressGooglePay: false,
    };
  }

  return {
    showRegularApplePay: !isExpressCheckout && applePayEligible,
    showExpressApplePay: isExpressCheckout && applePayEligible,
    showRegularGooglePay: !isExpressCheckout && googlePayEligible,
    showExpressGooglePay: isExpressCheckout && googlePayEligible,
  };
};
